import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { legalSections } from "@/i18n/legalSections";

const Section = ({ index, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className="border-t border-ink/10 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0"
  >
    <div className="flex items-center gap-3 mb-5">
      <span className="font-mono text-[11px] tracking-[0.4em] text-gold">{index}</span>
      <div className="h-px w-12 bg-gold" />
      <span className="overline">{title}</span>
    </div>
    <div className="space-y-4 text-[15px] md:text-base leading-relaxed text-ink/85 font-light">
      {children}
    </div>
  </motion.section>
);

const renderSection = (s) => {
  if (s.id === "provider") {
    return (
      <>
        <p className="font-display text-xl text-ink leading-snug">{s.lead}</p>
        <p className="text-muted_text">{s.sub}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-10 mt-4">
          {s.grid.map((g) => (
            <div key={g.label}>
              <div className="overline-soft mb-1.5">{g.label}</div>
              <p className="text-sm leading-relaxed text-muted_text font-light">
                {g.lines.map((l, i) => (
                  <span key={l}>
                    {l}
                    {i < g.lines.length - 1 && <br />}
                  </span>
                ))}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  }
  if (s.id === "contact") {
    return (
      <ul className="space-y-2.5">
        {s.items.map((item) => (
          <li key={item.label} className="flex flex-wrap items-baseline gap-x-3">
            <span className="overline-soft min-w-[110px]">{item.label}</span>
            <a
              href={item.href}
              target={item.href.startsWith("http") ? "_blank" : undefined}
              rel={item.href.startsWith("http") ? "noreferrer" : undefined}
              className="text-ink hover:text-gold transition-colors"
            >
              {item.value}
            </a>
          </li>
        ))}
      </ul>
    );
  }
  // Default paragraphs (string or {text, link, after})
  return s.paragraphs.map((p, i) => {
    if (typeof p === "string") return <p key={i}>{p}</p>;
    return (
      <p key={i}>
        {p.text}
        <a
          href={p.link.href}
          target="_blank"
          rel="noreferrer"
          className="text-ink underline decoration-gold/50 underline-offset-4 hover:text-gold transition-colors"
        >
          {p.link.label}
        </a>
        {p.after}
      </p>
    );
  });
};

export default function LegalNoticePage() {
  const { t, lang } = useT();
  const sections = legalSections[lang] || legalSections.en;

  return (
    <main
      data-testid="legal-notice-page"
      className="relative bg-soft text-ink pt-32 lg:pt-40 pb-28 md:pb-40 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-ice_blue/30 blur-3xl opacity-60" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ice_blue to-transparent" />
      </div>

      <div className="container-luxe">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] text-muted_text hover:text-ink transition-colors mb-12"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.4} />
          {t("legal.backHome")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-gold" />
            <span className="overline">{t("legal.overline")}</span>
          </div>

          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance leading-[1.02]">
            {t("legal.title1")}
            <br />
            <span className="italic font-light text-ink/90">{t("legal.title2")}</span>
          </h1>

          <p className="mt-8 text-base md:text-lg leading-relaxed text-muted_text font-light">
            {t("legal.lead")}
          </p>
        </motion.div>

        <div className="mt-16 max-w-3xl">
          {sections.map((s, i) => (
            <Section key={s.id} index={String(i + 1).padStart(2, "0")} title={s.title}>
              {renderSection(s)}
            </Section>
          ))}

          <div className="mt-16 pt-8 border-t border-ink/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-muted_text">
              {t("legal.lastUpdated")} · {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] text-ink hover:text-gold transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.4} />
              {t("legal.returnLabel")}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
